import { useEffect, useState, useId } from "react";
import axios from "axios";
import {
  Calculator,
  Send,
  CheckCircle2,
  AlertCircle,
  Plus,
  Minus,
  Tag,
  ShieldCheck,
  TrendingDown,
  ChevronDown
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Reveal from "./Reveal";
import { PRODUCTS_CATALOG } from "@/constants/productsCatalog";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function QuoteSection() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith("en");
  const numLocale = isEn ? "en-US" : "es-ES";
  const eur = (v) =>
    new Intl.NumberFormat(numLocale, { style: "currency", currency: "EUR" }).format(v);

  const getProductName = (p) => (isEn ? p?.nameEn || p?.titleEn || p?.name : p?.name) || "";
  const getProductCategory = (p) => (isEn ? p?.categoryEn || p?.category : p?.category) || "";
  const getProductUnit = (p) => {
    const u = p?.unit || "unidades";
    if (!isEn) return u;
    if (u === "unidades") return "units";
    if (u === "packs") return "packs";
    return u;
  };

  // Selected product ID (default to first catalog item or copas)
  const [selectedProductId, setSelectedProductId] = useState(
    PRODUCTS_CATALOG[0]?.id || "pack-6-copas-de-cava-ariana"
  );

  // Quantity: Strictly minimum 1 (min="1")
  const [qty, setQty] = useState(100);

  // Form state
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    details: "",
    honeypot: ""
  });
  const [gdpr, setGdpr] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Accessibility IDs
  const productSelectId = useId();
  const qtyInputId = useId();
  const nameId = useId();
  const companyId = useId();
  const phoneId = useId();
  const emailId = useId();
  const detailsId = useId();
  const gdprId = useId();

  // Listen to product selection events from the catalog or modal
  useEffect(() => {
    const handleProductSelect = (e) => {
      if (e.detail) {
        // Match product by ID with specialty alias support
        const mapAliases = {
          copas: "grabado-copas",
          corchos: "grabado-tapones",
          decantadores: "grabado-decantadores-vino"
        };
        const targetId = mapAliases[e.detail] || e.detail;
        const found = PRODUCTS_CATALOG.find((p) => p.id === targetId || p.id === e.detail);
        if (found) {
          setSelectedProductId(found.id);
        } else {
          setSelectedProductId(targetId);
        }
      }
    };
    window.addEventListener("luv:product", handleProductSelect);
    return () => window.removeEventListener("luv:product", handleProductSelect);
  }, []);

  // Find active product details
  const activeProduct =
    PRODUCTS_CATALOG.find((p) => p.id === selectedProductId) ||
    PRODUCTS_CATALOG[0] || {
      id: "copas",
      name: "Copas de Cristal",
      reference: "MK-001",
      basePrice: 2.66,
      unit: "unidades"
    };

  // Safe numerical quantity: strictly min 1, no decimals or negatives
  const safeQty = Math.max(1, Math.floor(Math.abs(Number(qty) || 1)));

  // Volume discount rule: >= 100 units gives 5% discount
  // Formula: precioTotal = (cantidad >= 100) ? (precioBase * cantidad * 0.95) : (precioBase * cantidad)
  const isDiscount = safeQty >= 100;
  const basePrice = activeProduct.basePrice || 2.66;
  const subtotal = basePrice * safeQty;
  const total = isDiscount ? basePrice * safeQty * 0.95 : subtotal;
  const unitPriceEffective = isDiscount ? basePrice * 0.95 : basePrice;
  const totalSavings = subtotal - total;

  // Percentage slider fill (scale from 1 to 2500)
  const sliderFill = Math.min(100, Math.max(0, ((safeQty - 1) / (2500 - 1)) * 100));

  // Stepper handlers
  const handleDecreaseQty = () => {
    setQty((prev) => Math.max(1, (Number(prev) || 1) - (isDiscount ? 10 : 1)));
  };

  const handleIncreaseQty = () => {
    setQty((prev) => Math.min(2500, (Number(prev) || 1) + (safeQty >= 100 ? 10 : 5)));
  };

  const handleQtyInputChange = (e) => {
    const val = e.target.value;
    if (val === "") {
      setQty("");
      return;
    }
    const parsed = parseInt(val, 10);
    if (!isNaN(parsed)) {
      setQty(Math.max(1, Math.min(2500, Math.abs(parsed))));
    }
  };

  const setFormField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errorMsg) setErrorMsg("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!gdpr) {
      toast.error(t("quote.gdprError", "Debes aceptar la política de privacidad para continuar."));
      return;
    }

    setSending(true);
    setErrorMsg("");

    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        company: form.company.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        product: activeProduct.id,
        product_name: getProductName(activeProduct),
        reference: activeProduct.reference || "",
        quantity: safeQty,
        unit: getProductUnit(activeProduct),
        details: form.details.trim(),
        honeypot: form.honeypot,
        privacy_accepted: true
      });
      setSent(true);
      toast.success(t("quote.successToast", "Presupuesto solicitado con éxito"));
    } catch (err) {
      const errDetail =
        err.response?.data?.detail ||
        t("quote.errorMsg", "No se pudo enviar la solicitud. Inténtalo de nuevo o escríbenos por WhatsApp.");
      setErrorMsg(errDetail);
      toast.error(errDetail);
    } finally {
      setSending(false);
    }
  };

  const handleResetForm = () => {
    setSent(false);
    setForm({
      name: "",
      company: "",
      phone: "",
      email: "",
      details: "",
      honeypot: ""
    });
    setGdpr(false);
  };

  return (
    <section
      id="presupuesto"
      data-testid="quote-section"
      className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 scroll-mt-20"
    >
      <Reveal className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#F5CB5C] font-semibold bg-[#24020B]/60 border border-[#F5CB5C]/20 px-4 py-1.5 rounded-full">
          <Calculator size={14} className="text-[#F5CB5C]" />
          <span>{t("quote.kicker", "Calculadora & Presupuesto Directo")}</span>
        </div>
        <h2 className="mt-4 font-display text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
          {t("quote.titleMain", "Pide tu presupuesto")}{" "}
          <span className="text-gold-gradient">{t("quote.titleHighlight", "sin compromiso")}</span>
        </h2>
        <p className="mt-4 text-sm sm:text-base text-gray-400 leading-relaxed">
          {t(
            "quote.desc",
            "Selecciona cualquier artículo de nuestro catálogo, introduce las unidades aproximadas y calcula al instante tu tarifa con descuento por volumen."
          )}
        </p>
      </Reveal>

      <div className="mt-12 grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
        {/* Left Column: Product Selection, Quantity Slider & Dynamic Pricing Breakdown */}
        <Reveal>
          <div
            data-testid="calculator-card"
            className="glass-card rounded-3xl p-6 sm:p-8 relative overflow-hidden border-white/10"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(245,203,92,0.08),transparent_55%)] pointer-events-none" />

            <div className="relative space-y-6">
              {/* Product Selector Dropdown */}
              <div>
                <label
                  htmlFor={productSelectId}
                  className="block text-[11px] uppercase tracking-[0.24em] text-gray-400 font-semibold mb-2"
                >
                  {t("quote.selectProductLabel", "Selecciona los productos *")}
                </label>
                <div className="relative">
                  <select
                    id={productSelectId}
                    data-testid="select-product-dropdown"
                    value={selectedProductId}
                    onChange={(e) => setSelectedProductId(e.target.value)}
                    className="field-dark w-full py-3.5 pl-4 pr-10 appearance-none text-white bg-[#0E121A] rounded-2xl cursor-pointer text-sm font-medium border-white/15 focus:border-[#F5CB5C]"
                  >
                    {PRODUCTS_CATALOG.map((p) => (
                      <option key={p.id} value={p.id} className="bg-[#0B0E14] text-gray-200 py-2">
                        [{p.reference}] {getProductName(p)} — {eur(p.basePrice)}/{getProductUnit(p)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={18}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#F5CB5C] pointer-events-none"
                  />
                </div>

                {/* Selected Product Micro-Card */}
                <div className="mt-3 p-3.5 rounded-2xl bg-[#05070A]/60 border border-white/5 flex items-center gap-3.5">
                  <img
                    src={activeProduct.image}
                    alt={getProductName(activeProduct)}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/laser-engraving-glass.jpg";
                    }}
                    className="w-12 h-12 rounded-xl object-cover border border-white/10 shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-[#F5CB5C] bg-[#F5CB5C]/10 px-2 py-0.5 rounded-md">
                        {activeProduct.reference}
                      </span>
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider">
                        {getProductCategory(activeProduct)}
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-white truncate mt-0.5">
                      {getProductName(activeProduct)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-[10px] text-gray-400 block">
                      {t("quote.unitBaseLabel", "Base unitario")}
                    </span>
                    <span className="font-display text-sm text-[#FAE19C] font-bold">
                      {eur(basePrice)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quantity Selector: strictly min="1" */}
              <div className="pt-2 border-t border-white/5">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <label
                    htmlFor={qtyInputId}
                    className="text-[11px] uppercase tracking-[0.24em] text-gray-400 font-semibold"
                  >
                    {t("quote.approxQtyLabel", "Cantidad total aproximada *")}
                  </label>
                  {isDiscount ? (
                    <span
                      data-testid="badge-volume-discount"
                      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 px-3 py-1 text-xs font-bold text-emerald-300 animate-pulse"
                    >
                      <TrendingDown size={13} />
                      −5% {t("quote.volumeDiscountBadge", "Descuento por volumen")}
                    </span>
                  ) : (
                    <span className="text-[11px] text-amber-300/80 font-medium">
                      {t("quote.addMoreForDiscount", "Añade {{count}} más para −5%", { count: 100 - safeQty })}
                    </span>
                  )}
                </div>

                {/* Number input and +/- mobile-friendly buttons */}
                <div className="mt-3 flex items-center gap-3">
                  <div className="relative flex-1">
                    <input
                      id={qtyInputId}
                      data-testid="input-quantity-number"
                      type="number"
                      min="1"
                      max="2500"
                      step="1"
                      value={qty}
                      onChange={handleQtyInputChange}
                      className="field-dark w-full py-3.5 text-center font-display text-2xl sm:text-3xl font-bold text-white tracking-wide rounded-2xl bg-[#0E121A]"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">
                      {getProductUnit(activeProduct)}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5 bg-[#05070A] border border-white/10 rounded-2xl p-1 shrink-0">
                    <button
                      type="button"
                      data-testid="btn-quote-decrease"
                      onClick={handleDecreaseQty}
                      disabled={safeQty <= 1}
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/10 active:scale-95 transition-all disabled:opacity-30 disabled:pointer-events-none"
                      aria-label={t("quote.decreaseQty", "Disminuir cantidad")}
                    >
                      <Minus size={18} />
                    </button>
                    <button
                      type="button"
                      data-testid="btn-quote-increase"
                      onClick={handleIncreaseQty}
                      disabled={safeQty >= 2500}
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/10 active:scale-95 transition-all disabled:opacity-30 disabled:pointer-events-none"
                      aria-label={t("quote.increaseQty", "Aumentar cantidad")}
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>

                {/* Range Slider (min="1") */}
                <div className="mt-5">
                  <label htmlFor="quote-range-slider" className="sr-only">
                    {t("quote.sliderAria", "Desplazador de cantidad para {{name}}", { name: getProductName(activeProduct) })}
                  </label>
                  <input
                    id="quote-range-slider"
                    data-testid="calculator-units-slider"
                    type="range"
                    min="1"
                    max="2500"
                    step="1"
                    value={safeQty}
                    onChange={(e) => setQty(Number(e.target.value))}
                    className="gold-range w-full"
                    style={{ "--fill": `${sliderFill}%` }}
                    aria-label={t("quote.approxQtyLabel", "Cantidad total aproximada *")}
                  />
                  <div className="mt-2 flex justify-between text-[11px] text-gray-500 font-mono">
                    <span>1 {t("quote.unitsShort", "u.")} ({t("quote.minLabel", "Mín")})</span>
                    <span className={safeQty >= 100 ? "text-[#F5CB5C] font-semibold" : ""}>100 {t("quote.unitsShort", "u.")} (−5%)</span>
                    <span>500 {t("quote.unitsShort", "u.")}</span>
                    <span>{(1000).toLocaleString(numLocale)} {t("quote.unitsShort", "u.")}</span>
                    <span>{(2500).toLocaleString(numLocale)}+ {t("quote.unitsShort", "u.")}</span>
                  </div>
                </div>
              </div>

              {/* Dynamic Price Breakdown Card */}
              <div
                data-testid="pricing-breakdown-card"
                className="rounded-2xl border border-[#F5CB5C]/25 bg-gradient-to-br from-[#24020B]/60 to-[#0A0D14]/80 p-5 sm:p-6 space-y-4"
              >
                <div className="text-[10px] uppercase tracking-[0.24em] text-gray-400 font-semibold flex items-center justify-between">
                  <span>{t("quote.breakdownTitle", "Desglose estimado del presupuesto")}</span>
                  <span className="text-[#F5CB5C] flex items-center gap-1">
                    <Tag size={12} /> {t("quote.indicativeRate", "Tarifa orientativa")}
                  </span>
                </div>

                <div className="space-y-2 text-xs sm:text-sm">
                  {/* Unit price row */}
                  <div className="flex justify-between items-center text-gray-300">
                    <span>{t("quote.baseUnitPrice", "Precio unitario base:")}</span>
                    <span className="font-mono text-white">
                      {eur(basePrice)} / {t("quote.perUnitWord", "ud.")}
                    </span>
                  </div>

                  {/* Volume discount row */}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">{t("quote.appliedDiscount", "Descuento aplicado:")}</span>
                    {isDiscount ? (
                      <span className="font-semibold text-emerald-400 flex items-center gap-1">
                        −5% (−{eur(totalSavings)})
                      </span>
                    ) : (
                      <span className="text-gray-400 font-mono">
                        0% ({t("quote.validFrom100", "Válido desde 100 u.")})
                      </span>
                    )}
                  </div>

                  {/* Effective price row */}
                  <div className="flex justify-between items-center text-gray-300 border-t border-white/5 pt-2">
                    <span>{t("quote.effectiveUnitPrice", "Precio unitario resultante:")}</span>
                    <span className="font-mono font-semibold text-[#FAE19C]">
                      {eur(unitPriceEffective)} / {t("quote.perUnitWord", "ud.")}
                    </span>
                  </div>

                  {/* Total Estimated row */}
                  <div className="border-t border-[#F5CB5C]/20 pt-3 flex items-baseline justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                        <span>{t("quote.estimatedTotal", "Total estimado")}</span>
                        {activeProduct?.isApproximatePrice && (
                          <span className="text-[9px] font-bold text-[#05070A] bg-gradient-to-r from-[#FAE19C] to-[#F5CB5C] px-2 py-0.5 rounded-full">
                            {t("quote.approxPriceBadge", "Precio aprox.")}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {activeProduct?.isApproximatePrice
                          ? t("quote.vatApproxNote", "+ IVA · Precio aproximado según modelo")
                          : t("quote.vatApplicableNote", "+ IVA aplicable")}
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        data-testid="calculator-estimated-price"
                        className="font-display text-3xl sm:text-4xl text-gold-gradient font-bold tracking-tight"
                      >
                        {activeProduct?.isApproximatePrice ? "~" : ""}{eur(total)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-white/5 flex items-center gap-2 text-[11px] text-gray-400">
                  <ShieldCheck size={14} className="text-[#F5CB5C] shrink-0" />
                  <span>
                    {t(
                      "quote.guaranteeNote",
                      "Sin compromiso de compra. Incluye muestra digital y asesoramiento técnico."
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Right Column: Contact & Proposal Form */}
        <Reveal delay={0.12}>
          <div
            data-testid="quote-form-card"
            className="glass-card rounded-3xl p-6 sm:p-8 relative overflow-hidden border-white/10"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(163,29,61,0.15),transparent_55%)] pointer-events-none" />

            {/* Success Notification Banner */}
            {sent ? (
              <div
                data-testid="quote-form-success"
                className="relative my-4 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6 sm:p-8 text-center space-y-4"
              >
                <CheckCircle2 size={44} className="text-emerald-400 mx-auto" />
                <h3 className="font-display text-white text-xl sm:text-2xl font-bold">
                  {t("quote.successTitle", "¡Solicitud enviada correctamente!")}
                </h3>
                <p className="text-xs sm:text-sm text-emerald-200 leading-relaxed max-w-md mx-auto">
                  {t(
                    "quote.successDesc",
                    "Hemos recibido tu petición para {{qty}} {{unit}} de {{product}}. Nuestro equipo comercial se pondrá en contacto contigo en menos de 24 horas laborables.",
                    {
                      qty: safeQty,
                      unit: getProductUnit(activeProduct),
                      product: getProductName(activeProduct)
                    }
                  )}
                </p>
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={handleResetForm}
                    className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-xs font-semibold text-[#05070A] bg-white hover:bg-gray-200 transition-colors"
                  >
                    {t("quote.requestAnother", "Solicitar otro presupuesto")}
                  </button>
                </div>
              </div>
            ) : (
              <form
                data-testid="quote-form"
                onSubmit={submit}
                className="relative space-y-4"
                noValidate={false}
              >
                {/* Honeypot field (hidden from real users) */}
                <input
                  type="text"
                  name="b_quote_hp"
                  value={form.honeypot}
                  onChange={setFormField("honeypot")}
                  tabIndex={-1}
                  autoComplete="off"
                  className="hidden"
                  aria-hidden="true"
                />

                <div className="text-[10px] uppercase tracking-[0.24em] text-gray-400 font-semibold mb-1">
                  {t("quote.contactHeader", "Datos de contacto comercial")}
                </div>

                {/* Error Banner */}
                {errorMsg && (
                  <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3.5 flex items-start gap-2.5 text-red-200 text-xs">
                    <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                {/* Name & Company */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={nameId}
                      className="block text-xs font-semibold text-gray-300 mb-1.5"
                    >
                      {t("quote.nameLabel", "Nombre y Apellidos")} *
                    </label>
                    <input
                      id={nameId}
                      data-testid="quote-form-name"
                      required
                      minLength={2}
                      placeholder={t("quote.namePlaceholder", "Ej: Juan Gómez")}
                      value={form.name}
                      onChange={setFormField("name")}
                      className="field-dark w-full py-3"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={companyId}
                      className="block text-xs font-semibold text-gray-300 mb-1.5"
                    >
                      {t("quote.companyLabel", "Empresa o Bodega")}
                    </label>
                    <input
                      id={companyId}
                      data-testid="quote-form-company"
                      placeholder={t("quote.companyPlaceholder", "Ej: Bodegas San Isidro / Particular")}
                      value={form.company}
                      onChange={setFormField("company")}
                      className="field-dark w-full py-3"
                    />
                  </div>
                </div>

                {/* Phone & Email */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={phoneId}
                      className="block text-xs font-semibold text-gray-300 mb-1.5"
                    >
                      {t("quote.phoneLabel", "Teléfono")} *
                    </label>
                    <input
                      id={phoneId}
                      data-testid="quote-form-phone"
                      required
                      minLength={6}
                      type="tel"
                      placeholder={t("quote.phonePlaceholder", "Ej: 600 000 000")}
                      value={form.phone}
                      onChange={setFormField("phone")}
                      className="field-dark w-full py-3"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={emailId}
                      className="block text-xs font-semibold text-gray-300 mb-1.5"
                    >
                      {t("quote.emailLabel", "Correo Electrónico")} *
                    </label>
                    <input
                      id={emailId}
                      data-testid="quote-form-email"
                      required
                      type="email"
                      placeholder={t("quote.emailPlaceholder", "Ej: contacto@bodega.com")}
                      value={form.email}
                      onChange={setFormField("email")}
                      className="field-dark w-full py-3"
                    />
                  </div>
                </div>

                {/* Comments / Details */}
                <div>
                  <label
                    htmlFor={detailsId}
                    className="block text-xs font-semibold text-gray-300 mb-1.5"
                  >
                    {t("quote.detailsLabel", "Detalles del proyecto o grabado")}
                  </label>
                  <textarea
                    id={detailsId}
                    data-testid="quote-form-details"
                    rows={3}
                    placeholder={t(
                      "quote.detailsPlaceholder",
                      "Cuéntanos si dispones de logotipo en alta resolución, acabados deseados o cualquier duda..."
                    )}
                    value={form.details}
                    onChange={setFormField("details")}
                    className="field-dark w-full resize-none py-3"
                  />
                </div>

                {/* GDPR Checkbox */}
                <div className="pt-2">
                  <label
                    htmlFor={gdprId}
                    className="flex items-start gap-3 text-xs text-gray-400 cursor-pointer select-none"
                  >
                    <input
                      id={gdprId}
                      data-testid="quote-form-gdpr"
                      type="checkbox"
                      required
                      checked={gdpr}
                      onChange={(e) => setGdpr(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded accent-[#F5CB5C] cursor-pointer"
                    />
                    <span>
                      {t("quote.gdprLabel", "Acepto el tratamiento de mis datos para recibir la propuesta comercial.")} *
                    </span>
                  </label>
                </div>

                {/* Submit button */}
                <button
                  data-testid="b2b-form-submit-button"
                  type="submit"
                  disabled={sending || !gdpr}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-bold text-[#05070A] bg-gradient-to-br from-[#FAE19C] via-[#F5CB5C] to-[#C69222] hover:shadow-[0_0_32px_rgba(245,203,92,0.45)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send size={16} />
                  {sending ? t("quote.sendingBtn", "Enviando solicitud...") : t("quote.submitBtn", "Solicitar presupuesto formal")}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
